import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { api, apiErrorMessage } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { Button } from "../components/ui/button";
import WalletPicker from "../components/WalletPicker";
import SupportTickets from "../components/SupportTickets";
import PendingContractReview from "../components/PendingContractReview";
import OfferedVolumeEditor from "../components/OfferedVolumeEditor";
import ChangeRequestPanel, { LockedNotice, OfferedVolumeLocked } from "../components/ChangeRequestPanel";
import CancellationRequestModal from "../components/CancellationRequestModal";
import RebatePreview from "../components/RebatePreview";
import ContractIter3Confirm from "../components/ContractIter3Confirm";
import PlayerIter3Settlements from "../components/PlayerIter3Settlements";
import Chats from "../components/Chats";
import AccountDeletionPanel from "../components/AccountDeletionPanel";
import DashboardTabs from "../components/DashboardTabs";
import { Wallet, ShieldCheck, Info, Trophy, Timer, Coins, PieChart } from "lucide-react";

// Iter 138 — Player dashboard content grouped into tabs.
// Tabs: Overview · Contracts · Rebate & Payout · Referrals · Proofs · Chats · Support · Settings.
// Gold pending-action badges hydrated from GET /me/pending-actions
// (contracts_pending_confirmation → Contracts, proofs_rejected → Proofs).
// Modals (terminate / cancel-iter3 / contact) remain at page-level so they
// overlay any tab. State is kept in the parent so triggers inside tabs work.

function Pill({ tone = "default", children, testId }) {
  const tones = {
    default: "border-white/20 text-white",
    warn: "border-[#F5B841]/50 text-[#F5B841]",
    ok: "border-emerald-500/50 text-emerald-400",
    danger: "border-[#F26D6D]/60 text-[#F26D6D]",
  };
  return <span data-testid={testId} className={`inline-flex items-center gap-1.5 border px-2.5 py-1 text-[11px] font-mono uppercase tracking-widest ${tones[tone]}`}>{children}</span>;
}

export default function PlayerDashboard() {
  const { user, refresh } = useAuth();
  const { t, locale } = useI18n();
  const [me, setMe] = useState(user);
  const [refs, setRefs] = useState([]);
  const [payouts, setPayouts] = useState([]);
  const [rewards, setRewards] = useState(null);
  const [contracts, setContracts] = useState({ active: [], past: [] });
  const [termModal, setTermModal] = useState(null); // {id, reason, note}
  const [cancelModal, setCancelModal] = useState(null); // Iter 123 — iter3 cancellation contract row
  const [contactModal, setContactModal] = useState(null); // {subject, body}

  const submitContact = async () => {
    if (!contactModal) return;
    try {
      await api.post("/players/me/contact", { subject: contactModal.subject, body: contactModal.body });
      toast.success(t("dash.contact.sent"));
      setContactModal(null);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
    }
  };

  const loadContracts = useCallback(async () => {
    try { const { data } = await api.get("/players/me/contracts"); setContracts(data); } catch (e) {}
  }, []);
  useEffect(() => { loadContracts(); }, [loadContracts]);

  // Iter 116 — Edit lock state (verified + activeContract → change-request mode).
  const [editLock, setEditLock] = useState(null);
  const loadEditLock = useCallback(async () => {
    try { const { data } = await api.get("/players/me/edit-lock"); setEditLock(data); } catch (e) {}
  }, []);
  useEffect(() => { loadEditLock(); }, [loadEditLock]);

  const submitTermination = async () => {
    if (!termModal) return;
    try {
      await api.post(`/players/me/contracts/${termModal.id}/terminate`, { reason: termModal.reason, note: termModal.note || undefined });
      toast.success(t("dash.contracts.termRequested"));
      setTermModal(null); loadContracts();
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
    }
  };
  const toggleListing = async (vertical, listed) => {
    try {
      const { data } = await api.post("/players/me/relist", { vertical, listed });
      setMe((old) => ({ ...(old || {}), listedInMarketplace: data.listedInMarketplace }));
      toast.success(listed ? t("dash.relist.on") : t("dash.relist.off"));
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error((d && typeof d === "object" && d.message) || apiErrorMessage(e));
    }
  };
  const busyIn = (vertical) => contracts.active.some((c) => c.vertical === vertical);
  // Iter 155 — Three-state marketplace listing derivation per vertical.
  //   (a) OPTED_OUT : listedInMarketplace[v] === false  (explicit)
  //   (b) BOUND     : editLock.boundByVertical[v]       (open chat OR live contract)
  //   (c) LISTED    : otherwise  (absent OR true = default-listed)
  // Precedence: BOUND wins over LISTED. OPTED_OUT only when literally false AND not bound.
  const listingStateOf = (vertical) => {
    const m = me?.listedInMarketplace;
    const explicitFalse = m && typeof m === "object" && m[vertical] === false;
    const bound = !!(editLock?.boundByVertical || {})[vertical];
    if (bound) return "bound";
    if (explicitFalse) return "opted_out";
    return "listed";
  };
  const listedIn = (vertical) => listingStateOf(vertical) === "listed";
  const hasActiveOrClosing = contracts.active.length > 0;
  const [wallet, setWallet] = useState(user?.cryptoWallet || "");
  const [walletCoin, setWalletCoin] = useState(user?.walletCoin || "USDT");
  const [walletNetwork, setWalletNetwork] = useState(user?.walletNetwork || "ERC20");
  const [walletTag, setWalletTag] = useState(user?.walletTag || "");
  const [walletConfirmed, setWalletConfirmed] = useState(false);
  const [busy, setBusy] = useState(false);
  // Iter 46 — additional-proof upload post-onboarding.
  const [addlProof, setAddlProof] = useState(null);
  const [addlBusy, setAddlBusy] = useState(false);
  const [addlProofs, setAddlProofs] = useState([]);
  const uploadAdditionalProof = async () => {
    if (!addlProof) { toast.error("Choose a file"); return; }
    // Iter 49 — client-side pre-check that mirrors the backend PROOF_ALLOWED
    // caps so we fail fast (backend still authoritative via stream_validate_proof).
    const CAPS = {
      "image/jpeg": 10, "image/png": 10,
      "application/pdf": 25,
      "video/mp4": 50, "video/quicktime": 50, "video/webm": 50,
    };
    const mime = addlProof.type || "";
    const capMB = CAPS[mime];
    if (!capMB) {
      toast.error("Unsupported file type. Accepted: JPG, PNG, PDF, MP4, MOV, WEBM.");
      return;
    }
    if (addlProof.size > capMB * 1024 * 1024) {
      toast.error(`File is too large (${(addlProof.size / (1024*1024)).toFixed(1)} MB, cap ${capMB} MB).`);
      return;
    }
    const fd = new FormData();
    fd.append("file", addlProof);
    setAddlBusy(true);
    try {
      await api.post("/players/me/proofs", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Proof uploaded — any declined vertical is now back in the admin review queue.");
      setAddlProof(null);
      const { data } = await api.get(`/players/${user.id}`);
      setAddlProofs(data.proofFiles || []);
      setMe(data);
      refresh();
      // Iter 138 — proof upload can drop `proofs_rejected` counter → refresh badge.
      window.__vgRefreshPendingActions?.();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = (detail && typeof detail === "object" && detail.message) || apiErrorMessage(e);
      toast.error(msg);
    } finally { setAddlBusy(false); }
  };

  const load = useCallback(async () => {
    try {
      const [{ data: mine }, { data: r }, { data: p }, { data: rw }] = await Promise.all([
        api.get(`/players/${user.id}`), api.get("/referrals"), api.get("/payouts"), api.get("/players/me/rewards"),
      ]);
      setMe(mine); setRefs(r); setPayouts(p); setRewards(rw);
      setAddlProofs(mine.proofFiles || []);
      setWallet(mine.cryptoWallet || "");
      setWalletCoin(mine.walletCoin || "USDT");
      setWalletNetwork(mine.walletNetwork || "ERC20");
      setWalletTag(mine.walletTag || "");
      setWalletConfirmed(false);
    } catch (e) { /* ignore */ }
  }, [user.id]);

  useEffect(() => { load(); }, [load]);

  const saveWallet = async () => {
    setBusy(true);
    try {
      await api.patch("/players/me", {
        cryptoWallet: wallet, walletCoin, walletNetwork,
        walletTag: walletNetwork === "XRPL" ? walletTag : "",
        walletConfirmed: walletConfirmed,
      });
      toast.success("Wallet updated");
      load();
    } catch (e) { toast.error(apiErrorMessage(e)); } finally { setBusy(false); }
  };

  // Iter 50 — old `POST /players/me/screenshot` upload path removed from the
  // UI. Endpoint remains wired server-side for backward-compat but is no
  // longer reachable from any client surface. Reference volume is now
  // admin-only (Player Detail modal → PATCH /players/{id}/admin).

  const tier = me?.verificationTier || "unverified";
  const status = me?.status || "pending";
  // Iter 48 — per-vertical badges on the player header. Reads the same
  // tri-state as the admin console; legacy casino tier fallback is honoured
  // for read-time compat until a migration retires the tier field.
  const _vv = me?.verticalVerification || {};
  const casinoSet = Object.prototype.hasOwnProperty.call(_vv, "casino");
  const caseSet   = Object.prototype.hasOwnProperty.call(_vv, "case_opening");
  const legacyCasinoVerified = !casinoSet && ["verified", "vip"].includes(tier) && status === "approved";
  const casinoState = casinoSet ? (_vv.casino ? "verified" : "declined") : (legacyCasinoVerified ? "verified" : "pending");
  const caseState   = caseSet ? (_vv.case_opening ? "verified" : "declined") : "pending";
  const stateWord = (s) => {
    if (locale === "de") return s === "verified" ? "Verifiziert" : s === "declined" ? "Abgelehnt" : "Ausstehend";
    if (locale === "es") return s === "verified" ? "Verificado" : s === "declined" ? "Rechazado" : "Pendiente";
    return s === "verified" ? "Verified" : s === "declined" ? "Declined" : "Pending";
  };
  const stateTone = (s) => s === "verified" ? "ok" : s === "declined" ? "danger" : "warn";

  // ---------------------------------------------------------------
  // Tab nodes — relocation only, every button/handler preserved.
  // ---------------------------------------------------------------

  const overviewNode = (
    <>
      {/* Verification pills (per-vertical state) */}
      <div className="flex flex-wrap gap-2" data-testid="player-verticals">
        <Pill testId="player-casino-state" tone={stateTone(casinoState)}>Casino: {stateWord(casinoState)}</Pill>
        <Pill testId="player-case-state" tone={stateTone(caseState)}>Case-Pack: {stateWord(caseState)}</Pill>
      </div>

      {/* Edit-lock branch (Overview side):
            - direct-edit  → OfferedVolumeEditor
            - locked       → LockedNotice + OfferedVolumeLocked display +
                             ChangeRequestPanel (Iter 141: change-request now
                             lives directly below the locked banner). */}
      {editLock?.canEditDirect ? (
        <OfferedVolumeEditor me={me} onRefresh={() => { load(); loadEditLock(); }} />
      ) : editLock ? (
        <>
          <LockedNotice hasActiveContract={!!editLock.hasActiveContract} />
          <OfferedVolumeLocked me={me} />
          {(editLock.canSubmitChangeRequest || editLock.pendingChangeRequest) ? (
            <ChangeRequestPanel me={me} lock={editLock} onSubmitted={() => { load(); loadEditLock(); }} />
          ) : null}
        </>
      ) : (
        <OfferedVolumeEditor me={me} onRefresh={load} />
      )}

      {/* Wager categories tile — self-reported dollar amounts, info-only. */}
      {me?.wagerCategoriesSelfReported && (
        <section className="border border-white/15 p-6 lg:p-8" data-testid="wager-categories-tile">
          <div className="flex items-center gap-2 mb-1"><PieChart className="w-4 h-4 text-[#2DD4A7]" /><h2 className="font-display font-bold text-lg">{t("dash.wagerCategories.title")}</h2></div>
          <p className="text-xs text-[#F5B841] mb-5">{t("dash.wagerCategories.infoOnly")}</p>
          {(() => {
            const src = me.wagerCategoriesSelfReported || {};
            const keys = ["slots","house_originals","table_games","live_casino","sports","dice"];
            const values = keys.map((k) => Number((k === "dice" ? (src.dice ?? src.other) : src[k]) || 0));
            const maxVal = Math.max(...values, 0);
            return (
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
                {keys.map((k, i) => {
                  const v = values[i];
                  const barPct = maxVal > 0 ? (v / maxVal) * 100 : 0;
                  return (
                    <div key={k} data-testid={`wc-${k}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{t(`categories.${k}`)}</span>
                        <span
                          className="font-mono tabular-nums text-[#E8ECF4]"
                          style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                        >${v.toLocaleString()}</span>
                      </div>
                      <div className="h-1.5 bg-white/5 relative overflow-hidden">
                        <div className="absolute inset-y-0 left-0 bg-[#2DD4A7]/70" style={{ width: `${barPct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            );
          })()}
        </section>
      )}

      {/* Iter 150 — Case wager tile. SEPARATE per-vertical block, never
          mixed with the casino block. Case is stored as a single scalar
          `caseWagerSelfReported` (Iter 33 flattened; all 4 case
          coefficients are 1.0). Falls back to the legacy 4-key dict for
          older docs by summing. Reference / display only — never enters
          any billing or rebate math. */}
      {me && (
        <section className="border border-white/15 p-6 lg:p-8" data-testid="case-wager-tile">
          <div className="flex items-center gap-2 mb-1"><PieChart className="w-4 h-4 text-[#2DD4A7]" /><h2 className="font-display font-bold text-lg">{t("dash.caseWager.title")}</h2></div>
          <p className="text-xs text-[#F5B841] mb-5">{t("dash.caseWager.infoOnly")}</p>
          {(() => {
            const scalar = Number(me.caseWagerSelfReported || 0);
            // Back-compat: if the legacy 4-key dict is still present on
            // an older doc, sum it and prefer the scalar when both exist.
            const legacy = me.caseCategoriesSelfReported;
            const legacySum = legacy && typeof legacy === "object"
              ? ["case_opening","case_battles","pack_draw","upgrader"].reduce((s,k)=> s + Number(legacy[k] || 0), 0)
              : 0;
            const v = scalar > 0 ? scalar : legacySum;
            const hasValue = v > 0;
            return (
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div data-testid="cw-case-wager">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("dash.caseWager.label")}</span>
                    <span
                      className="font-mono tabular-nums text-[#E8ECF4]"
                      style={{ fontFamily: "'JetBrains Mono', ui-monospace, monospace" }}
                    >{hasValue ? `$${v.toLocaleString()}` : t("dash.caseWager.notSet")}</span>
                  </div>
                  <div className="h-1.5 bg-white/5 relative overflow-hidden">
                    <div className="absolute inset-y-0 left-0 bg-[#2DD4A7]/70" style={{ width: hasValue ? "100%" : "0%" }} />
                  </div>
                </div>
              </div>
            );
          })()}
        </section>
      )}
    </>
  );

  const contractsNode = (
    <>
      <PendingContractReview />
      <ContractIter3Confirm />
      <PlayerIter3Settlements />

      {/* Contracts list (active + past) with relist toggles + contact button */}
      <section className="border border-white/15 p-6 lg:p-8" data-testid="contracts-section">
        <div className="flex items-center gap-2 mb-1"><Coins className="w-4 h-4 text-[#2DD4A7]" /><h2 className="font-display font-bold text-lg">{t("dash.contracts.title")}</h2></div>
        <p className="text-xs text-[#8B97AE] mb-5">{t("dash.contracts.body")}</p>
        <div className="mb-5 flex flex-wrap items-center gap-3">
          {["casino", "case_opening"].map((v) => {
            const state = listingStateOf(v);   // "listed" | "opted_out" | "bound"
            const vLabel = v === "casino" ? "Casino" : "Case-Pack";
            const isBound = state === "bound";
            const isListed = state === "listed";
            const btnClass = isListed
              ? "bg-[#2DD4A7] text-[#04150F] border-[#2DD4A7]"
              : isBound
                ? "border-[#F5B841]/40 text-[#F5B841] bg-[#F5B841]/[0.06]"
                : "border-white/15 text-[#E8ECF4] hover:bg-white/5";
            const label =
              state === "bound"    ? t("dash.relist.bound").replace("{v}", vLabel)
            : state === "listed"   ? t("dash.relist.listed").replace("{v}", vLabel)
            :                        t("dash.relist.notListed").replace("{v}", vLabel);
            return (
              <button
                key={v}
                data-testid={`relist-toggle-${v}`}
                data-listing-state={state}
                disabled={isBound || me?.status !== "approved"}
                onClick={() => toggleListing(v, !isListed)}
                className={`px-3 h-9 border font-mono text-[10px] uppercase tracking-widest transition-colors ${btnClass} disabled:opacity-60 disabled:cursor-not-allowed`}
              >{vLabel}: {label}</button>
            );
          })}
          {(listingStateOf("casino") === "bound" || listingStateOf("case_opening") === "bound") && (
            <span className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid="relist-bound-note">{t("dash.relist.boundNote")}</span>
          )}
          <button data-testid="contact-btn" onClick={() => setContactModal({ subject: "", body: "" })} className="ml-auto px-3 h-9 border border-white/20 font-mono text-[10px] uppercase tracking-widest text-[#E8ECF4] hover:bg-white/5">{t("dash.contact.open")}</button>
        </div>
        <h3 className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-2">{t("dash.contracts.active")}</h3>
        {contracts.active.length === 0 && <div className="text-xs text-[#8B97AE] mb-4" data-testid="contracts-active-empty">{t("dash.contracts.emptyActive")}</div>}
        {contracts.active.map((c) => (
          <div key={c.id} className="border border-white/10 p-4 mb-3" data-testid={`contract-active-${c.id}`}>
            <div className="flex flex-wrap items-baseline gap-x-4 gap-y-1">
              <span className="font-display font-bold">{c.streamerName}</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{c.casinoName} · {c.vertical}</span>
              <span className="text-[10px] font-mono uppercase tracking-widest text-[#2DD4A7]">{c.status}</span>
              <span className="ml-auto text-[10px] font-mono text-[#8B97AE]">since {(c.signupDate||'').slice(0,10)}</span>
            </div>
            <div className="mt-2 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs">
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">ACV</div><div className="tabular-nums font-mono">${c.committedVolume.toLocaleString()}</div></div>
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Tracked</div><div className="tabular-nums font-mono">${c.trackedVolume.toLocaleString()}</div></div>
              <div><div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Progress</div><div className="tabular-nums font-mono">{c.committedVolume ? Math.round((c.trackedVolume/c.committedVolume)*100) : 0}%</div></div>
            </div>
            {c.status === "active" && c.flow !== "iter3" && (
              <button data-testid={`contract-end-${c.id}`} onClick={() => setTermModal({ id: c.id, reason: "streamer_non_delivery", note: "" })} className="mt-3 px-3 h-8 border border-[#F26D6D]/50 text-[#F26D6D] font-mono text-[10px] uppercase tracking-widest hover:bg-[#F26D6D]/10 transition-colors">{t("dash.contracts.endDeal")}</button>
            )}
            {c.flow === "iter3" && (
              <div className="mt-3 flex items-center gap-3" data-testid={`iter3-info-${c.id}`}>
                <span className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">Iter3 contract · manage from the agreement panel above</span>
                {c.status === "active" && (
                  <button
                    data-testid={`cxl-open-${c.id}`}
                    onClick={() => setCancelModal(c)}
                    className="ml-auto px-3 h-8 border border-[#F26D6D]/50 text-[#F26D6D] font-mono text-[10px] uppercase tracking-widest hover:bg-[#F26D6D]/10 transition-colors"
                  >Request cancellation</button>
                )}
              </div>
            )}
            {c.status === "closing" && (<div className="mt-3 text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">{t("dash.contracts.closingNotice")}</div>)}
          </div>
        ))}
        <h3 className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mt-6 mb-2">{t("dash.contracts.past")}</h3>
        {contracts.past.length === 0 && <div className="text-xs text-[#8B97AE]" data-testid="contracts-past-empty">{t("dash.contracts.emptyPast")}</div>}
        {contracts.past.map((c) => (
          <div key={c.id} className="border border-white/5 p-3 mb-2 text-xs font-mono flex flex-wrap gap-x-3 gap-y-1" data-testid={`contract-past-${c.id}`}>
            <span className="text-[#E8ECF4]">{c.streamerName}</span>
            <span className="text-[#8B97AE]">{c.casinoName} · {c.vertical}</span>
            <span className="text-[#F5B841]">{c.status}</span>
            <span className="ml-auto text-[#8B97AE] tabular-nums">${c.trackedVolume.toLocaleString()} tracked · rebate ${c.loyaltyRebateDue.toLocaleString()}</span>
          </div>
        ))}
      </section>
    </>
  );

  const rebateNode = (
    <>
      <RebatePreview />

      {/* Iter 172 — Raffle eligibility notice. Logged-in player portal
          ONLY. This is a rules/eligibility statement — no raffle engine,
          no ticket / entry / draw tracking. Raffles are run manually by
          admins; this card just tells the player how eligibility works. */}
      <section
        className="border border-[#F5B841]/30 bg-[#F5B841]/[0.05] p-6"
        data-testid="player-raffle-notice"
      >
        <div className="flex items-center gap-2 mb-2">
          <Trophy className="w-4 h-4 text-[#F5B841]" />
          <div className="text-[10px] font-mono uppercase tracking-widest text-[#F5B841]" data-testid="player-raffle-eyebrow">
            {t("dash.raffle.eyebrow")}
          </div>
        </div>
        <h2 className="font-display font-bold text-lg mb-2">{t("dash.raffle.title")}</h2>
        <p className="text-sm text-[#8B97AE] leading-relaxed" data-testid="player-raffle-body">
          {t("dash.raffle.body")}
        </p>
      </section>

      {/* Rewards wallet card (with inline next-milestone tile) */}
      <section className="border border-[#2DD4A7]/25 bg-[#2DD4A7]/[0.03] p-6 lg:p-8" data-testid="player-rewards-tile">
        <div className="flex items-start justify-between flex-wrap gap-6">
          <div className="flex-1 min-w-[240px]">
            <div className="flex items-center gap-2 mb-2"><Trophy className="w-4 h-4 text-[#2DD4A7]" /><h2 className="font-display font-bold text-lg">{t("dash.rewards.title")}</h2></div>
            <p className="text-xs text-[#8B97AE] mb-4">{t("dash.rewards.desc").replace("{pct}", rewards ? Math.round(rewards.flatRatePct * 100) : 10)}</p>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("dash.rewards.trackedVolume")}</div>
                <div className="font-mono text-2xl text-[#E8ECF4] tabular-nums" data-testid="rw-tracked-volume">{rewards ? rewards.trackedVolume.toLocaleString() : "0"}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("dash.rewards.totalEarned")}</div>
                <div className="font-mono text-2xl text-[#2DD4A7] tabular-nums" data-testid="rw-total-earned">{rewards ? rewards.totalLoyaltyEarned.toLocaleString() : "0"}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("dash.rewards.paidOut")}</div>
                <div className="font-mono text-2xl text-[#E8ECF4] tabular-nums" data-testid="rw-total-paid">{rewards ? rewards.totalPaid.toLocaleString() : "0"}</div>
              </div>
              <div>
                <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("dash.rewards.pending")}</div>
                <div className="font-mono text-2xl text-[#F5B841] tabular-nums" data-testid="rw-total-pending">{rewards ? rewards.totalPending.toLocaleString() : "0"}</div>
              </div>
            </div>
          </div>
          <div className="w-full sm:w-auto sm:min-w-[220px] border border-white/10 bg-[#0B0E14] p-4">
            <div className="flex items-center gap-2 mb-2"><Timer className="w-3.5 h-3.5 text-[#F5B841]" /><div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE]">{t("dash.rewards.nextMilestone")}</div></div>
            {rewards?.nextMilestone ? (
              <div data-testid="rw-next-milestone">
                <div className="font-display text-3xl font-black text-[#E8ECF4] tabular-nums">{rewards.nextMilestone.days}d</div>
                <div className="text-xs text-[#8B97AE] mt-1 font-mono">{t("dash.rewards.due")} <span className="text-[#E8ECF4]">{rewards.nextMilestone.dueAt}</span></div>
                <div className="text-xs text-[#2DD4A7] mt-1 font-mono">{t("dash.rewards.daysRemaining")
                  .replace("{n}", rewards.nextMilestone.daysRemaining)
                  .replace(/\{s\}/g, rewards.nextMilestone.daysRemaining === 1 ? "" : (locale === "de" ? "e" : "s"))}</div>
              </div>
            ) : (
              <div className="text-xs text-[#8B97AE]" data-testid="rw-no-milestone">
                {rewards && rewards.activeReferralCount === 0 ? t("dash.rewards.noReferrals") : t("dash.rewards.allRecorded")}
              </div>
            )}
          </div>
        </div>
      </section>

      {/* Payout wallet card */}
      <section className="border border-white/8 p-6 lg:p-8">
        <div className="flex items-center gap-2 mb-2"><Wallet className="w-4 h-4 text-[#2DD4A7]" /><h2 className="font-display font-bold text-lg">{t("dash.payoutWallet.title")}</h2></div>
        <p className="text-xs text-[#8B97AE] mb-5">{t("dash.payoutWallet.desc")}</p>
        {(() => {
          const hasWallet = !!me?.cryptoWallet;
          const hasDueRebate = payouts.some((p) => p.type === "loyalty_rebate" && p.status === "due");
          if (hasDueRebate && !hasWallet) {
            return (
              <div className="mb-5 border border-[#F5B841]/40 bg-[#F5B841]/5 p-3 flex items-start gap-2 text-xs" data-testid="rebate-wallet-prompt">
                <Info className="w-3.5 h-3.5 mt-0.5 shrink-0 text-[#F5B841]" />
                <div className="text-[#F5B841]">{t("dash.payoutWallet.rebateWaiting")}</div>
              </div>
            );
          }
          return null;
        })()}
        <WalletPicker
          idPrefix="player-wallet"
          address={wallet}
          network={walletNetwork}
          coin={walletCoin}
          tag={walletTag}
          confirmed={walletConfirmed}
          onAddress={setWallet}
          onNetwork={setWalletNetwork}
          onCoin={setWalletCoin}
          onTag={setWalletTag}
          onConfirm={setWalletConfirmed}
        />
        <Button data-testid="player-wallet-save" onClick={saveWallet} disabled={busy} className="mt-4 rounded-none bg-[#2DD4A7] text-[#04150F] hover:bg-[#F5B841] hover:text-[#04150F] h-10 px-5 font-semibold transition-colors">{t("common.save")}</Button>
      </section>

      {/* Payouts table */}
      <section className="border border-white/15 p-6 lg:p-8">
        <h2 className="font-display font-bold text-lg mb-5">{t("dash.payouts")}</h2>
        {payouts.length === 0 ? <div className="text-sm text-[#8B97AE]" data-testid="player-no-payouts">{t("dash.noPayoutsYet")}</div> : (
          <table className="w-full text-sm" data-testid="player-payouts">
            <thead className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]">
              <tr className="border-b border-white/10"><th className="text-left py-2 pr-4">Type</th><th className="text-left py-2 pr-4">Amount</th><th className="text-left py-2 pr-4">Currency</th><th className="text-left py-2 pr-4">Status</th></tr>
            </thead>
            <tbody>
              {payouts.map((p) => (
                <tr key={p.id} className="border-b border-white/5"><td className="py-2 pr-4">{p.type}</td><td className="py-2 pr-4 font-mono">{p.amount}</td><td className="py-2 pr-4">{p.currency}</td><td className="py-2 pr-4"><Pill tone={p.status === "paid" ? "ok" : p.status === "failed" ? "danger" : "warn"}>{p.status}</Pill></td></tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </>
  );

  const referralsNode = (
    <>
      {/* Referral hub (copy link + stats + progress) */}
      <section className="border border-white/15 p-6 lg:p-8" data-testid="referral-hub">
        <div className="flex items-center gap-2 mb-1"><Coins className="w-4 h-4 text-[#2DD4A7]" /><h2 className="font-display font-bold text-lg">{t("dash.referral.title")}</h2></div>
        <p className="text-xs text-[#8B97AE] mb-4">{t("dash.referral.explain")}</p>
        {rewards?.referralLink && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <div className="border border-white/15 px-3 py-2 font-mono text-xs text-[#E8ECF4] flex-1 truncate" data-testid="referral-link">{rewards.referralLink}</div>
            <button
              type="button"
              data-testid="referral-copy"
              onClick={() => { navigator.clipboard.writeText(rewards.referralLink); toast.success(t("dash.referral.copied")); }}
              className="px-4 h-9 border border-[#2DD4A7] bg-[#2DD4A7] text-[#04150F] font-mono text-[11px] uppercase tracking-widest hover:bg-[#F5B841] hover:border-[#F5B841] transition-colors"
            >{t("dash.referral.copy")}</button>
            {/* Iter 201 — Copy-invite-text button. Assembles the localized
                invite body at copy time and injects the player's real
                referral URL into the {link} placeholder. Fully i18n'd
                (referral.inviteText / referral.copyInviteText / referral.inviteTextCopied)
                so EN/DE/ES all resolve from the shared dict — no inline conditionals. */}
            <button
              type="button"
              data-testid="referral-copy-invite"
              onClick={() => {
                const body = t("referral.inviteText").replace("{link}", rewards.referralLink);
                navigator.clipboard.writeText(body);
                toast.success(t("referral.inviteTextCopied"));
              }}
              className="px-4 h-9 border border-[#2DD4A7] bg-transparent text-[#2DD4A7] font-mono text-[11px] uppercase tracking-widest hover:bg-[#2DD4A7]/10 transition-colors"
            >{t("referral.copyInviteText")}</button>
          </div>
        )}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("dash.referral.verified")}</div>
            <div className="font-mono text-2xl text-[#2DD4A7] tabular-nums" style={{fontFamily:"'JetBrains Mono',ui-monospace,monospace"}} data-testid="referral-verified-count">{rewards?.verifiedReferralCount ?? 0}</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("dash.referral.pending")}</div>
            <div className="font-mono text-2xl text-[#F5B841] tabular-nums" style={{fontFamily:"'JetBrains Mono',ui-monospace,monospace"}} data-testid="referral-pending-count">{rewards?.pendingReferralCount ?? 0}</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("dash.referral.yourRate")}</div>
            <div className="font-mono text-2xl text-[#E8ECF4] tabular-nums" style={{fontFamily:"'JetBrains Mono',ui-monospace,monospace"}} data-testid="referral-effective-rate">{Math.round((rewards?.effectiveRebateRate ?? 0.10) * 100)}%</div>
          </div>
          <div>
            <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mb-1">{t("dash.referral.cap")}</div>
            <div className="font-mono text-2xl text-[#8B97AE] tabular-nums" style={{fontFamily:"'JetBrains Mono',ui-monospace,monospace"}}>{Math.round((0.10 + (rewards?.referralCap ?? 5) * (rewards?.referralStepPct ?? 0.01)) * 100)}%</div>
          </div>
        </div>
        {(() => {
          const v = rewards?.verifiedReferralCount ?? 0;
          const cap = rewards?.referralCap ?? 5;
          if (v >= cap) return <p className="mt-4 text-xs text-[#2DD4A7] font-mono uppercase tracking-widest">{t("dash.referral.atCap")}</p>;
          const toNext = 1;
          const nextRate = Math.round((0.10 + (v + 1) * (rewards?.referralStepPct ?? 0.01)) * 100);
          const toCap = cap - v;
          return (
            <p className="mt-4 text-xs text-[#8B97AE]" data-testid="referral-progress">
              {t("dash.referral.toNext").replace("{n}", toNext).replace("{rate}", nextRate)} · {t("dash.referral.toCap").replace("{n}", toCap)}
            </p>
          );
        })()}
        <p className="mt-4 text-[10px] font-mono uppercase tracking-widest text-[#F5B841]">{t("dash.referral.verifiedOnlyNote")}</p>
      </section>

      {/* Iter 152 — People THIS player has referred. Sits directly under
          the counter so the "1" and the person (e.g. Findax) reconcile
          visually. STRICT alias-only display: no email, wallet, or any
          other PII from the referred player is ever loaded here. */}
      <section className="border border-white/15 p-6 lg:p-8" data-testid="referred-players-tile">
        <div className="flex items-center gap-2 mb-1"><ShieldCheck className="w-4 h-4 text-[#2DD4A7]" /><h2 className="font-display font-bold text-lg">{t("dash.referredPlayers.title")}</h2></div>
        <p className="text-xs text-[#8B97AE] mb-5">{t("dash.referredPlayers.explain")}</p>
        {(!rewards?.referredPlayers || rewards.referredPlayers.length === 0) ? (
          <div className="text-sm text-[#8B97AE]" data-testid="referred-players-empty">{t("dash.referredPlayers.empty")}</div>
        ) : (
          <div className="overflow-x-auto" data-testid="referred-players-list">
            <table className="w-full text-sm">
              <thead className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]">
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 pr-4">{t("dash.referredPlayers.col.alias")}</th>
                  <th className="text-left py-2 pr-4">{t("dash.referredPlayers.col.joined")}</th>
                  <th className="text-left py-2 pr-4">{t("dash.referredPlayers.col.status")}</th>
                </tr>
              </thead>
              <tbody>
                {rewards.referredPlayers.map((rp, idx) => (
                  <tr key={`${rp.alias}-${idx}`} className="border-b border-white/5" data-testid={`referred-player-row-${idx}`}>
                    <td className="py-2 pr-4 text-[#E8ECF4]" data-testid={`referred-player-alias-${idx}`}>{rp.alias}</td>
                    <td className="py-2 pr-4 font-mono text-xs text-[#8B97AE]">{(rp.joinedAt || "").slice(0, 10) || "—"}</td>
                    <td className="py-2 pr-4">
                      <Pill tone={rp.status === "verified" ? "ok" : "warn"} testId={`referred-player-status-${idx}`}>
                        {rp.status === "verified" ? t("dash.referredPlayers.status.verified") : t("dash.referredPlayers.status.pending")}
                      </Pill>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Loyalty rebate log table */}
      <section className="border border-white/15 p-6 lg:p-8" data-testid="loyalty-rebate-view">
        <div className="flex items-center gap-2 mb-1"><Coins className="w-4 h-4 text-[#2DD4A7]" /><h2 className="font-display font-bold text-lg">{t("dash.loyaltyRebate.title")}</h2></div>
        {/* Iter 202 — Commission explainer (dash.loyaltyRebate.flatLabel + dash.loyaltyRebate.trackedOnly)
            removed from the player Referrals page. Keeps the heading and the
            "Waiting for your first match" empty state; feature/math unchanged. */}
        {(!rewards || rewards.activeReferralCount === 0 || !rewards.referrals || rewards.referrals.length === 0) ? (
          <div className="border border-white/10 bg-white/[0.02] p-6 text-center" data-testid="loyalty-rebate-empty">
            <div className="font-mono text-[10px] uppercase tracking-widest text-[#F5B841] mb-2">{t("dash.loyaltyRebate.empty.eyebrow")}</div>
            <div className="text-sm text-[#8B97AE]">{t("dash.loyaltyRebate.empty.body")}</div>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]">
                <tr className="border-b border-white/10">
                  <th className="text-left py-2 pr-4">{t("dash.loyaltyRebate.col.signup")}</th>
                  <th className="text-left py-2 pr-4">{t("dash.loyaltyRebate.col.days")}</th>
                  <th className="text-left py-2 pr-4">{t("dash.loyaltyRebate.col.tracked")}</th>
                  <th className="text-left py-2 pr-4">{t("dash.loyaltyRebate.col.amount")}</th>
                  <th className="text-left py-2 pr-4">{t("dash.loyaltyRebate.col.status")}</th>
                  <th className="text-left py-2 pr-4">{t("dash.loyaltyRebate.col.tx")}</th>
                </tr>
              </thead>
              <tbody>
                {rewards.referrals.map((r) => {
                  const tone = r.status === "paid" ? "ok" : (r.status === "due" || r.status === "processing") ? "warn" : "default";
                  const daysColor = r.daysConfirmed >= 90 ? "text-[#2DD4A7]" : "text-[#E8ECF4]";
                  return (
                    <tr key={r.referralId} className="border-b border-white/5" data-testid={`loyalty-row-${r.referralId}`}>
                      <td className="py-2 pr-4 font-mono text-xs text-[#8B97AE]">{r.signupDate || "—"}</td>
                      <td className="py-2 pr-4 font-mono tabular-nums"><span className={daysColor} data-testid={`loyalty-days-${r.referralId}`}>{r.daysConfirmed}</span><span className="text-[#5A6478]"> / {r.daysToTarget}</span></td>
                      <td className="py-2 pr-4 font-mono tabular-nums text-[#E8ECF4]">{r.trackedVolume.toLocaleString()}</td>
                      <td className="py-2 pr-4 font-mono tabular-nums text-[#2DD4A7]" data-testid={`loyalty-amount-${r.referralId}`}>{r.rebateAmount.toLocaleString()}</td>
                      <td className="py-2 pr-4"><Pill tone={tone} testId={`loyalty-status-${r.referralId}`}>{t(`dash.loyaltyRebate.status.${r.status}`) || r.status}</Pill></td>
                      <td className="py-2 pr-4 font-mono text-[10px] text-[#8B97AE] max-w-[220px] truncate" data-testid={`loyalty-tx-${r.referralId}`}>{r.txReference || (r.status === "paid" ? "—" : "")}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* Iter 153 — Casino sign-ups (under-code) block hidden entirely
          when the `referrals` collection has no entries for this player.
          The empty-state row was confusing next to the always-visible
          "People you referred" block above; a missing section reads
          cleanly as "nothing to show yet." */}
      {refs.length > 0 && (
        <section className="border border-white/15 p-6 lg:p-8">
          <div className="flex items-center gap-2 mb-5"><ShieldCheck className="w-4 h-4 text-[#2DD4A7]" /><h2 className="font-display font-bold text-lg">{t("dash.referrals")}</h2></div>
          <div className="overflow-x-auto" data-testid="player-referrals">
            <table className="w-full text-sm">
              <thead className="text-[11px] font-mono uppercase tracking-widest text-[#8B97AE]">
                <tr className="border-b border-white/10"><th className="text-left py-2 pr-4">Casino</th><th className="text-left py-2 pr-4">Signup</th><th className="text-left py-2 pr-4">Under-code vol</th><th className="text-left py-2 pr-4">Status</th></tr>
              </thead>
              <tbody>
                {refs.map((r) => (
                  <tr key={r.id} className="border-b border-white/5">
                    <td className="py-2 pr-4">{r.casinoId}</td>
                    <td className="py-2 pr-4 font-mono text-xs">{(r.signupDate || "").slice(0, 10)}</td>
                    <td className="py-2 pr-4 font-mono">{r.underCodeVolumeCumulative}</td>
                    <td className="py-2 pr-4"><Pill tone={r.status === "flagged" ? "danger" : r.status === "completed" ? "ok" : "warn"}>{r.status}</Pill></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}
    </>
  );

  const proofsNode = (
    <>
      {/* Iter 141 — ChangeRequestPanel relocated to Overview (directly below
          the locked banner). Proofs tab keeps only the additional-proof
          upload for declined / follow-up verticals. */}

      {/* Additional proof upload — surfaces rejected verticals back to admin queue */}
      <section className="border border-[#F5B841]/30 bg-[#F5B841]/[0.03] p-6 lg:p-8" data-testid="player-addl-proof-card">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-[#F5B841]" />
          <h2 className="font-display font-bold text-lg">{locale === "de" ? "Zusätzliche Nachweise hochladen" : locale === "es" ? "Subir pruebas adicionales" : "Upload additional proof"}</h2>
        </div>
        <p className="text-xs text-[#8B97AE] mb-4 leading-relaxed">
          {locale === "de"
            ? "Lade jederzeit zusätzliche Nachweise hoch (Screenshots, Wager-Historie, Transaktionsbelege, kurze Screencasts). Wurde eine Kategorie abgelehnt, landet sie automatisch wieder in der Admin-Prüfung."
            : locale === "es"
            ? "Suba pruebas adicionales en cualquier momento (capturas, historial de wager, comprobantes de transacciones, screencasts cortos). Si algún vertical fue rechazado, subir aquí lo devuelve automáticamente a la cola de revisión del administrador."
            : "Upload additional evidence (screenshots, wager history, transaction receipts, short screencasts) any time after onboarding. If any of your verticals was declined, uploading here automatically puts them back in the admin's review queue."}
          <span className="text-[#F5B841]"> {locale === "de" ? "Kein Auto-Verify — ein Admin prüft jede neue Datei." : locale === "es" ? "Sin auto-verificación — un administrador revisa cada archivo nuevo." : "Uploading does NOT auto-verify — an admin still reviews each new file."}</span>
        </p>
        <div className="flex flex-wrap items-center gap-3">
          {/* Iter 205 — Native file-input text is replaced by an i18n-driven
              custom control. Native input is visually hidden and clicked via
              the label, so browser/OS locale text ("Durchsuchen…") never
              renders alongside site-locale copy. */}
          <label className="inline-flex items-center gap-2 px-3 h-10 border border-white/20 cursor-pointer hover:bg-white/5 transition-colors">
            <span className="text-[11px] font-mono uppercase tracking-widest text-[#E8ECF4]">{t("common.chooseFile")}</span>
            <input
              data-testid="player-addl-proof-file"
              type="file"
              accept="image/jpeg,image/png,application/pdf,video/mp4,video/quicktime,video/webm"
              onChange={(e) => setAddlProof(e.target.files?.[0] || null)}
              className="sr-only"
            />
          </label>
          <span
            data-testid="player-addl-proof-filename"
            className="text-[11px] font-mono text-[#8B97AE] truncate max-w-[240px]"
          >{addlProof ? addlProof.name : t("common.noFileChosen")}</span>
          <Button
            data-testid="player-addl-proof-submit"
            onClick={uploadAdditionalProof}
            disabled={addlBusy || !addlProof}
            className="rounded-none bg-[#F5B841] text-[#04150F] hover:bg-[#2DD4A7] hover:text-[#04150F] h-10 px-5 font-semibold transition-colors disabled:opacity-40"
          >{addlBusy ? (locale === "de" ? "Wird hochgeladen…" : locale === "es" ? "Subiendo…" : "Uploading…") : (locale === "de" ? "Nachweis hochladen" : locale === "es" ? "Subir prueba" : "Upload proof")}</Button>
        </div>
        <div className="text-[10px] font-mono uppercase tracking-widest text-[#8B97AE] mt-3" data-testid="player-addl-proof-caption">
          {locale === "de"
            ? "Akzeptiert: JPG, PNG (bis 10 MB), PDF (bis 25 MB), MP4, MOV, WEBM (bis 50 MB)"
            : locale === "es"
            ? "Aceptados: JPG, PNG (hasta 10 MB), PDF (hasta 25 MB), MP4, MOV, WEBM (hasta 50 MB)"
            : "Accepted: JPG, PNG (up to 10 MB), PDF (up to 25 MB), MP4, MOV, WEBM (up to 50 MB)"}
        </div>
        {addlProofs.length > 0 && (
          <ul className="mt-4 space-y-1" data-testid="player-addl-proof-list">
            {addlProofs.map((f) => (
              <li key={f.path} className="text-[11px] font-mono text-[#8B97AE] flex items-center gap-2">
                <span className="w-1.5 h-1.5 rounded-full bg-[#2DD4A7] inline-block" />
                <span className="text-[#E8ECF4]">{f.filename || f.path.split("/").pop()}</span>
                <span>· {(f.uploadedAt || "").replace("T", " ").slice(0, 19)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  );

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 lg:py-16">
      <div className="flex items-start justify-between flex-wrap gap-4 mb-10">
        <div>
          <div className="font-mono text-[11px] uppercase tracking-widest text-[#8B97AE] mb-2">Player</div>
          <h1 className="font-display text-4xl sm:text-5xl font-black tracking-tighter" data-testid="player-alias">{me?.alias}</h1>
        </div>
      </div>

      <DashboardTabs
        testidPrefix="player-tabs"
        roleLabel={t("tabs.role.player")}
        tabs={[
          { id: "overview",  label: t("tabs.player.overview"),  node: overviewNode },
          { id: "contracts", label: t("tabs.player.contracts"), badgeKey: "contracts_pending_confirmation", node: contractsNode },
          { id: "rebate",    label: t("tabs.player.cashback"),  node: rebateNode },
          { id: "referrals", label: t("tabs.player.referrals"), node: referralsNode },
          { id: "proofs",    label: t("tabs.player.proofs"),    badgeKey: "proofs_rejected", node: proofsNode },
          { id: "chats",     label: t("tabs.player.chats"),     node: <Chats me={me ? { id: me.id, role: "player" } : null} /> },
          { id: "support",   label: t("tabs.player.support"),   node: <SupportTickets /> },
          { id: "settings",  label: t("tabs.player.settings"),  node: <AccountDeletionPanel role="player" /> },
        ]}
      />

      {/* Modals (page-level so they overlay any tab) */}
      {termModal && (
        <div className="fixed inset-0 z-[80] bg-black/75 flex items-start justify-center p-6" data-testid="term-modal" onClick={() => setTermModal(null)}>
          <div className="bg-[#0B0E14] border border-white/15 max-w-md w-full mt-16" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10"><h2 className="font-display text-xl font-black tracking-tighter">{t("dash.contracts.endDeal")}</h2><p className="text-xs text-[#8B97AE] mt-2">{t("dash.contracts.termAgreementNote")}</p></div>
            <div className="p-5 space-y-3">
              <select data-testid="term-reason" value={termModal.reason} onChange={(e) => setTermModal({ ...termModal, reason: e.target.value })} className="w-full rounded-none bg-transparent border border-white/20 h-10 text-sm text-[#E8ECF4]">
                <option value="streamer_non_delivery">{t("dash.contracts.reasonNonDelivery")}</option>
                <option value="streamer_non_response">{t("dash.contracts.reasonNonResponse")}</option>
                <option value="other">{t("dash.contracts.reasonOther")}</option>
              </select>
              <textarea data-testid="term-note" rows={3} maxLength={200} value={termModal.note} onChange={(e) => setTermModal({ ...termModal, note: e.target.value.slice(0,200) })} placeholder={t("dash.contracts.notePlaceholder")} className="w-full rounded-none bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-3 resize-none" />
            </div>
            <div className="p-5 border-t border-white/10 flex justify-end gap-3">
              <button data-testid="term-cancel" onClick={() => setTermModal(null)} className="px-4 h-10 border border-white/15 font-mono text-xs uppercase tracking-widest text-[#E8ECF4] hover:bg-white/10 transition-colors">{t("common.cancel")}</button>
              <button data-testid="term-submit" onClick={submitTermination} className="px-4 h-10 bg-[#F26D6D] text-white font-mono text-xs uppercase tracking-widest hover:bg-[#F5B841] hover:text-[#04150F] transition-colors">{t("dash.contracts.endDeal")}</button>
            </div>
          </div>
        </div>
      )}

      {cancelModal && (
        <CancellationRequestModal
          contract={cancelModal}
          onClose={() => setCancelModal(null)}
          onSubmitted={() => { setCancelModal(null); loadContracts(); load(); }}
        />
      )}

      {contactModal && (
        <div className="fixed inset-0 z-[80] bg-black/75 flex items-start justify-center p-6" data-testid="contact-modal" onClick={() => setContactModal(null)}>
          <div className="bg-[#0B0E14] border border-white/15 max-w-md w-full mt-16" onClick={(e) => e.stopPropagation()}>
            <div className="p-5 border-b border-white/10"><h2 className="font-display text-xl font-black tracking-tighter">{t("dash.contact.title")}</h2><p className="text-xs text-[#8B97AE] mt-2">{t("dash.contact.body")}</p></div>
            <div className="p-5 space-y-3">
              <input data-testid="contact-subject" value={contactModal.subject} maxLength={120} onChange={(e) => setContactModal({ ...contactModal, subject: e.target.value })} placeholder={t("dash.contact.subject")} className="w-full rounded-none bg-transparent border border-white/20 h-10 text-sm text-[#E8ECF4] px-3" />
              <textarea data-testid="contact-body" rows={5} maxLength={2000} value={contactModal.body} onChange={(e) => setContactModal({ ...contactModal, body: e.target.value })} placeholder={t("dash.contact.placeholder")} className="w-full rounded-none bg-transparent border border-white/20 text-sm text-[#E8ECF4] p-3 resize-none" />
            </div>
            <div className="p-5 border-t border-white/10 flex justify-end gap-3">
              <button data-testid="contact-cancel" onClick={() => setContactModal(null)} className="px-4 h-10 border border-white/15 font-mono text-xs uppercase tracking-widest text-[#E8ECF4] hover:bg-white/10">{t("common.cancel")}</button>
              <button data-testid="contact-submit" onClick={submitContact} disabled={contactModal.subject.length < 2 || contactModal.body.length < 2} className="px-4 h-10 bg-[#2DD4A7] text-[#04150F] font-mono text-xs uppercase tracking-widest hover:bg-[#F5B841] disabled:opacity-40">{t("dash.contact.send")}</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
